import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import {
  CHART_LEGEND_CONFIG,
  CHART_TOOLTIP_CONFIG,
  IMPACTED_PEOPLE_COLORS,
} from '@/src/lib/chartConfig.js';
import { WidgetWrapper } from '@/src/components/projects/ProjectDashboard/WidgetWrapper.jsx';
import { WidgetInner } from '@/src/components/projects/ProjectDashboard/WidgetInner.jsx';
import { WidgetCard } from '@/src/components/projects/ProjectDashboard/WidgetCard.jsx';
import SentimentDot from '@/src/components/SentimentDot.jsx';
import { impactedPeopleWidgetContent } from '@/src/components/EmptyStates.jsx';
import Empty from '@/src/components/Empty.jsx';
import { buildPath } from '@/src/components/Link.jsx';
import { CHANGE_IMPACTS_INDEX } from '@/src/constants/routes.js';

export const ImpactedPeopleWidget = ({ data }) => {
  const navigate = useNavigate();
  const { slug, projectId } = useParams();

  const handleNavigateToChangeImpacts = () => {
    const path = buildPath(CHANGE_IMPACTS_INDEX, { slug, projectId });
    navigate(path);
  };

  const { groups, stakeholders, employees, externals } = data;
  const hasData =
    groups > 0 || stakeholders > 0 || employees > 0 || externals > 0;

  const chartData = {
    labels: ['Groups', 'Stakeholders', 'Employees', 'Externals'],
    datasets: [
      {
        data: [groups, stakeholders, employees, externals],
        backgroundColor: [
          IMPACTED_PEOPLE_COLORS.groups,
          IMPACTED_PEOPLE_COLORS.stakeholders,
          IMPACTED_PEOPLE_COLORS.employees,
          IMPACTED_PEOPLE_COLORS.externals,
        ],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '50%',
    plugins: {
      legend: CHART_LEGEND_CONFIG,
      tooltip: {
        ...CHART_TOOLTIP_CONFIG,
        callbacks: {
          ...CHART_TOOLTIP_CONFIG.callbacks,
          title: () => null,
          label: function (context) {
            return `${context.label}: ${context.parsed}`;
          },
        },
      },
    },
  };

  return (
    <WidgetWrapper className="impacted-people-widget">
      <div className="flex align-center m-b-18">
        <h2>Impacted People Overview</h2>
      </div>
      {hasData ? (
        <WidgetInner height={365} className="flex flex-align-start">
          <div className="left">
            <WidgetCard sentiment="groups" className="p-x-20 p-y-16 m-b-16">
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="groups" />
                  <div className="font-size-16 font-semibold">Groups</div>
                </div>
                <div className="font-size-18 font-semibold">{groups}</div>
              </div>
            </WidgetCard>
            <WidgetCard
              sentiment="stakeholders"
              className="p-x-20 p-y-16 m-b-16"
            >
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="stakeholders" />
                  <div className="font-size-16 font-semibold">Stakeholders</div>
                </div>
                <div className="font-size-18 font-semibold">{stakeholders}</div>
              </div>
            </WidgetCard>
            <WidgetCard sentiment="employees" className="p-x-20 p-y-16 m-b-16">
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="employees" />
                  <div className="font-size-16 font-semibold">Employees</div>
                </div>
                <div className="font-size-18 font-semibold">{employees}</div>
              </div>
            </WidgetCard>
            <WidgetCard sentiment="externals" className="p-x-20 p-y-16 m-b-16">
              <div className="flex align-center flex-justify">
                <div className="flex align-center">
                  <SentimentDot sentiment="externals" />
                  <div className="font-size-16 font-semibold">Externals</div>
                </div>
                <div className="font-size-18 font-semibold">{externals}</div>
              </div>
            </WidgetCard>
          </div>
          <div className="right">
            <div style={{ height: '300px', width: '100%' }}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>
        </WidgetInner>
      ) : (
        <WidgetInner height={365} className="flex flex-col">
          <Empty
            simplified
            content={impactedPeopleWidgetContent}
            action={handleNavigateToChangeImpacts}
          />
        </WidgetInner>
      )}
    </WidgetWrapper>
  );
};
